// useNewsData.js - Version finale avec corrections de timing
import { useState, useEffect, useCallback } from 'react';
import newsFirebaseService from './NewsFirebaseService';

export const useNewsData = (userId, selectedTheme, searchQuery, selectedObjectiveForNews, NEWS_THEMES) => {
  // ================================
  // ÉTATS SIMPLIFIÉS
  // ================================
  
  const [newsByTheme, setNewsByTheme] = useState({});
  const [filteredNews, setFilteredNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoveryMessage, setDiscoveryMessage] = useState('');
  
  // Mode de recherche actuel
  const [currentSearchMode, setCurrentSearchMode] = useState('theme');
  const [customSearchResults, setCustomSearchResults] = useState([]);
  
  // États pour l'analyse sémantique
  const [analysisProgress, setAnalysisProgress] = useState({ 
    isAnalyzing: false, 
    progress: 0, 
    current: 0, 
    total: 0 
  });
  
  const [semanticAnalysisInfo, setSemanticAnalysisInfo] = useState({
    totalAnalyzed: 0,
    relevantFound: 0,
    avgScore: 0,
    analysisTime: 0,
    lastAnalysis: null
  });

  // États pour la persistance
  const [storedObjectiveResults, setStoredObjectiveResults] = useState(null);
  const [hasStoredResults, setHasStoredResults] = useState(false);
  const [loadingStoredResults, setLoadingStoredResults] = useState(false);

  // NOUVEAUX ÉTATS pour notifier les changements de données
  const [dataUpdated, setDataUpdated] = useState(false);
  const [lastUpdateType, setLastUpdateType] = useState(null);

  // ================================
  // FONCTION DE NOTIFICATION DES CHANGEMENTS
  // ================================

  const notifyDataUpdate = useCallback((updateType, details = {}) => {
    console.log(`📊 Notification mise à jour données: ${updateType}`, details);
    setDataUpdated(true);
    setLastUpdateType(updateType);
    
    // Reset après délai pour permettre aux composants de réagir
    setTimeout(() => {
      setDataUpdated(false);
      setLastUpdateType(null);
    }, 100);
  }, []);

  // ================================
  // CHARGEMENT RÉSULTATS STOCKÉS
  // ================================

  const loadStoredObjectiveResults = useCallback(async (objective) => {
    if (!objective || !objective.id || !userId) {
      return null;
    }

    try {
      setLoadingStoredResults(true);
      console.log(`📂 Chargement résultats stockés: ${objective.title}`);

      const storedResults = await newsFirebaseService.getLatestObjectiveResults(objective.id, userId);
      
      if (storedResults) {
        const storedNews = await newsFirebaseService.getNewsFromStoredIds(storedResults.selectedNewsIds || []);
        
        const completeResults = {
          ...storedResults,
          news: storedNews,
          isFromStorage: true
        };

        setStoredObjectiveResults(completeResults);
        setHasStoredResults(true);
        
        setSemanticAnalysisInfo(storedResults.analysisMetadata || {
          totalAnalyzed: 0,
          relevantFound: storedNews.length,
          avgScore: 0,
          analysisTime: 0,
          lastAnalysis: storedResults.createdAt
        });

        setFilteredNews(storedNews);
        
        // CORRECTION: Notifier que les données sont mises à jour
        notifyDataUpdate('stored_objective', { 
          objectiveId: objective.id, 
          articlesCount: storedNews.length 
        });
        
        return completeResults;
      } else {
        setStoredObjectiveResults(null);
        setHasStoredResults(false);
        return null;
      }

    } catch (error) {
      console.error('❌ Erreur chargement résultats stockés:', error);
      setStoredObjectiveResults(null);
      setHasStoredResults(false);
      return null;
    } finally {
      setLoadingStoredResults(false);
    }
  }, [userId, notifyDataUpdate]);

  // ================================
  // ANALYSE SÉMANTIQUE
  // ================================

  const performFastSemanticAnalysis = useCallback(async (objective, saveResults = true) => {
    if (!objective || !objective.title) {
      return [];
    }

    try {
      console.log(`🎯 Analyse sémantique: "${objective.title}"`);
      setLoading(true);
      setError(null);
      
      setAnalysisProgress({ 
        isAnalyzing: true, 
        progress: 10, 
        current: 0, 
        total: 0,
        message: 'Préparation analyse...'
      });
      
      const startTime = Date.now();
      
      const relevantNews = await newsFirebaseService.searchNewsForObjective(
        objective, 
        userId, 
        {
          maxResults: 50,
          minSemanticScore: 4,
          saveResults: saveResults,
          onProgress: (progress, current, total) => {
            setAnalysisProgress({
              isAnalyzing: true,
              progress: Math.min(progress, 95),
              current,
              total,
              message: `Analyse... ${current}/${total}`
            });
          }
        }
      );
      
      const analysisTime = Date.now() - startTime;
      const avgScore = relevantNews.length > 0 
        ? relevantNews.reduce((sum, article) => sum + (article.semanticScore || 0), 0) / relevantNews.length
        : 0;

      const analysisInfo = {
        totalAnalyzed: newsByTheme['all']?.length || 0,        
        relevantFound: relevantNews.length,
        avgScore: avgScore,
        analysisTime: analysisTime,
        lastAnalysis: new Date().toISOString()
      };
      
      setSemanticAnalysisInfo(analysisInfo);
      
      setAnalysisProgress({ 
        isAnalyzing: false, 
        progress: 100, 
        current: relevantNews.length, 
        total: relevantNews.length,
        message: 'Analyse terminée!'
      });

      setTimeout(() => {
        setAnalysisProgress({ 
          isAnalyzing: false, 
          progress: 0, 
          current: 0, 
          total: 0,
          message: ''
        });
      }, 2000);

      if (saveResults && relevantNews.length > 0) {
        try {
          const latestResults = await newsFirebaseService.getLatestObjectiveResults(objective.id, userId);
          if (latestResults) {
            const completeStoredResults = {
              ...latestResults,
              news: relevantNews,
              isFromStorage: true
            };
            setStoredObjectiveResults(completeStoredResults);
            setHasStoredResults(true);
          }
        } catch (storageError) {
          console.warn('⚠️ Erreur mise à jour stockage:', storageError);
        }
      }
      
      return relevantNews;
      
    } catch (error) {
      console.error('❌ Erreur analyse sémantique:', error);
      setError('Erreur analyse sémantique');
      setAnalysisProgress({ 
        isAnalyzing: false, 
        progress: 0, 
        current: 0, 
        total: 0,
        message: 'Erreur analyse'
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [userId, newsByTheme]);

  // ================================
  // CHARGEMENT THÈMES
  // ================================

  const loadNewsByTheme = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📰 Chargement thèmes...');
      
      const themes = NEWS_THEMES.map(theme => theme.id);
      const newsByThemeData = {};
      
      const themePromises = themes.map(async (theme) => {
        try {
          const themeNews = await newsFirebaseService.getNewsByTheme(theme, 25, userId);
          return { theme, news: themeNews };
        } catch (error) {
          console.warn(`Erreur thème ${theme}:`, error);
          return { theme, news: [] };
        }
      });
      
      const results = await Promise.all(themePromises);
      
      results.forEach(({ theme, news }) => {
        newsByThemeData[theme] = news;
      });
      
      const allGeneralNews = await newsFirebaseService.getAllNews(150, userId);
      newsByThemeData['all'] = allGeneralNews;
      
      setNewsByTheme(newsByThemeData);
      console.log('✅ Thèmes chargés');
      
    } catch (error) {
      console.error('❌ Erreur chargement thèmes:', error);
      setError('Erreur chargement actualités');
    } finally {
      setLoading(false);
    }
  }, [userId, NEWS_THEMES]);

  // ================================
  // DÉTECTION MODE
  // ================================

  const detectSearchMode = useCallback(() => {
    if (selectedObjectiveForNews) {
      return 'objective';
    }
    if (searchQuery && searchQuery.trim() && !selectedObjectiveForNews) {
      return 'custom';
    }
    return 'theme';
  }, [selectedObjectiveForNews, searchQuery]);

  // ================================
  // FILTRAGE INTELLIGENT AVEC NOTIFICATIONS
  // ================================

  useEffect(() => {
    const filterNews = async () => {
      try {
        const mode = detectSearchMode();
        setCurrentSearchMode(mode);
        
        console.log(`🎯 Mode: ${mode}`, {
          objectif: selectedObjectiveForNews?.title,
          recherche: searchQuery,
          theme: selectedTheme
        });

        // MODE OBJECTIF
        if (mode === 'objective') {
          console.log('📂 Mode objectif...');
          
          if (!hasStoredResults) {
            const storedResults = await loadStoredObjectiveResults(selectedObjectiveForNews);
            if (storedResults && storedResults.news?.length > 0) {
              console.log('✅ Résultats stockés chargés');
              const finalResults = searchQuery 
                ? filterNewsBySearch(storedResults.news, searchQuery)
                : storedResults.news;
              
              setFilteredNews(finalResults);
              setCustomSearchResults([]);
              
              // CORRECTION: Notification avec nouvelles données
              notifyDataUpdate('objective_loaded', { 
                articlesCount: finalResults.length,
                fromStorage: true 
              });
              return;
            }
          }

          if (hasStoredResults && storedObjectiveResults?.news) {
            console.log('📂 Utilisation données stockées');
            const finalResults = searchQuery 
              ? filterNewsBySearch(storedObjectiveResults.news, searchQuery)
              : storedObjectiveResults.news;
            
            setFilteredNews(finalResults);
            setCustomSearchResults([]);
            
            // Notification si recherche appliquée
            if (searchQuery) {
              notifyDataUpdate('search_applied', { 
                articlesCount: finalResults.length 
              });
            }
            return;
          }

          console.log('🧠 Analyse sémantique...');
          const semanticResults = await performFastSemanticAnalysis(selectedObjectiveForNews, true);
          
          const finalResults = searchQuery 
            ? filterNewsBySearch(semanticResults, searchQuery)
            : semanticResults;
          
          setFilteredNews(finalResults);
          setCustomSearchResults([]);
          
          // CORRECTION: Notification après analyse sémantique
          notifyDataUpdate('semantic_analysis', { 
            articlesCount: finalResults.length,
            objectiveId: selectedObjectiveForNews.id
          });
          
          return;
        }

        // MODE RECHERCHE PERSONNALISÉE
        if (mode === 'custom') {
          console.log('🔍 Mode recherche personnalisée...');
          
          const allAvailableNews = newsByTheme['all'] || [];
          const customResults = filterNewsBySearch(allAvailableNews, searchQuery);
          
          setFilteredNews(customResults);
          setCustomSearchResults(customResults);
          
          // Reset infos analyse
          setSemanticAnalysisInfo({
            totalAnalyzed: 0,
            relevantFound: 0,
            avgScore: 0,
            analysisTime: 0,
            lastAnalysis: null
          });
          setStoredObjectiveResults(null);
          setHasStoredResults(false);
          
          // CORRECTION: Notification pour recherche personnalisée
          notifyDataUpdate('custom_search', { 
            articlesCount: customResults.length,
            searchQuery: searchQuery
          });
          
          return;
        }

        // MODE THÉMATIQUE
        console.log('🎨 Mode thème...');
        
        let newsToFilter = selectedTheme === 'all' 
          ? newsByTheme['all'] || []
          : newsByTheme[selectedTheme] || [];
        
        const filtered = searchQuery ? filterNewsBySearch(newsToFilter, searchQuery) : newsToFilter;
        
        setFilteredNews(filtered);
        setCustomSearchResults([]);
        
        // Reset infos analyse
        setSemanticAnalysisInfo({
          totalAnalyzed: 0,
          relevantFound: 0,
          avgScore: 0,
          analysisTime: 0,
          lastAnalysis: null
        });
        setStoredObjectiveResults(null);
        setHasStoredResults(false);
        
        // Notification pour thème
        notifyDataUpdate('theme_filter', { 
          articlesCount: filtered.length,
          theme: selectedTheme
        });
        
      } catch (error) {
        console.error('❌ Erreur filtrage:', error);
        setError('Erreur filtrage');
        setFilteredNews([]);
      }
    };

    if (selectedObjectiveForNews || Object.keys(newsByTheme).length > 0) {
      filterNews();
    }
    
  }, [
    selectedTheme, 
    searchQuery, 
    selectedObjectiveForNews?.id, 
    newsByTheme, 
    hasStoredResults, 
    storedObjectiveResults, 
    performFastSemanticAnalysis, 
    detectSearchMode, 
    loadStoredObjectiveResults,
    notifyDataUpdate
  ]);

  // ================================
  // FILTRAGE TEXTUEL
  // ================================

  const filterNewsBySearch = useCallback((newsArray, query) => {
    if (!query || !query.trim()) return newsArray;
    
    const searchTerms = query.toLowerCase().split(' ').filter(term => term.length > 2);
    
    return newsArray.filter(article => {
      const searchableText = `${article.title} ${article.content} ${article.source}`.toLowerCase();
      return searchTerms.some(term => searchableText.includes(term));
    });
  }, []);

  // ================================
  // CHARGEMENT INITIAL
  // ================================

  const loadNews = useCallback(async () => {
    try {
      console.log('🚀 Chargement initial...');
      await loadNewsByTheme();
      console.log('✅ Chargement terminé');
    } catch (error) {
      console.error('❌ Erreur chargement:', error);
      setError('Erreur chargement initial');
    }
  }, [loadNewsByTheme]);

  // ================================
  // DÉCOUVERTE THÉMATIQUE
  // ================================

  const handleDiscoverTheme = useCallback(async (theme) => {
    try {
      setIsDiscovering(true);
      setDiscoveryMessage(`Découverte ${theme}...`);
      setCurrentSearchMode('theme');
      
      const result = await newsFirebaseService.discoverAndAddNews(theme, userId, {
        maxNews: 20
      });
      
      if (result.added > 0) {
        setDiscoveryMessage(`${result.added} nouvelles actualités ${theme}`);
        const updatedNews = await newsFirebaseService.getNewsByTheme(theme, 25, userId);
        setNewsByTheme(prev => ({ ...prev, [theme]: updatedNews }));
        
        // Notification découverte thème
        notifyDataUpdate('theme_discovery', { 
          theme: theme,
          added: result.added
        });
      } else {
        setDiscoveryMessage(`Aucune nouvelle actualité ${theme}`);
      }
      
      setTimeout(() => {
        setDiscoveryMessage('');
        setIsDiscovering(false);
      }, 3000);
      
    } catch (error) {
      console.error('❌ Erreur découverte thème:', error);
      setDiscoveryMessage('Erreur découverte');
      setTimeout(() => {
        setDiscoveryMessage('');
        setIsDiscovering(false);
      }, 3000);
    }
  }, [userId, notifyDataUpdate]);

  // ================================
  // DÉCOUVERTE POUR OBJECTIF
  // ================================

  const handleDiscoverForObjective = useCallback(async (objective, forceRefresh = false) => {
    if (!objective || !objective.title) return { success: false };
    
    try {
      setIsDiscovering(true);
      setDiscoveryMessage(`${forceRefresh ? 'Actualisation' : 'Découverte'} "${objective.title}"...`);
      
      console.log('=== ENRICHISSEMENT OBJECTIF ===');
      
      if (!forceRefresh && !hasStoredResults) {
        const existingResults = await loadStoredObjectiveResults(objective);
        if (existingResults && existingResults.news?.length > 0) {
          console.log('✅ Résultats existants trouvés');
          setDiscoveryMessage('Résultats précédents chargés');
          setTimeout(() => {
            setDiscoveryMessage('');
            setIsDiscovering(false);
          }, 2000);
          return { 
            success: true, 
            fromStorage: true,
            message: 'Résultats précédents chargés',
            dataReady: true 
          };
        }
      }

      console.log('🔍 Découverte + analyse complète');
      
      const result = await newsFirebaseService.discoverAndAnalyzeForObjective(
        objective, 
        userId, 
        {
          maxNews: 50,
          saveResults: true
        }
      );
      
      console.log('✅ Découverte terminée:', result);
      
      // Recharger données
      await loadNewsByTheme();
      await new Promise(resolve => setTimeout(resolve, 1000));
      await loadStoredObjectiveResults(objective);
      
      const successMessage = result.success 
        ? `${result.enrichment?.added || 0} actualités ajoutées, ${result.analysis?.relevant || 0} pertinentes`
        : 'Aucune nouvelle actualité trouvée';
        
      setDiscoveryMessage(successMessage);
      
      // CORRECTION: Notification découverte objectif AVEC nouvelles données
      if (result.success) {
        notifyDataUpdate('objective_discovery', { 
          objectiveId: objective.id,
          added: result.enrichment?.added || 0,
          relevant: result.analysis?.relevant || 0
        });
      }
      
      return { 
        success: result.success, 
        added: result.enrichment?.added || 0,
        relevant: result.analysis?.relevant || 0,
        message: successMessage,
        dataReady: true
      };
      
    } catch (error) {
      console.error('❌ Erreur découverte objectif:', error);
      setDiscoveryMessage('Erreur découverte');
      return { 
        success: false, 
        error: error.message,
        dataReady: false
      };
    } finally {
      setTimeout(() => {
        setDiscoveryMessage('');
        setIsDiscovering(false);
      }, 4000);
    }
  }, [userId, loadNewsByTheme, loadStoredObjectiveResults, hasStoredResults, notifyDataUpdate]);

  // ================================
  // DÉCOUVERTE PERSONNALISÉE CORRIGÉE
  // ================================

  const handleCustomDiscovery = useCallback(async (searchQuery, detectedTheme = 'entrepreneurship') => {
    if (!searchQuery?.trim()) {
      return { success: false, dataReady: false, message: 'Requête vide' };
    }

    try {
      setIsDiscovering(true);
      setDiscoveryMessage(`Recherche: "${searchQuery}"...`);
      setCurrentSearchMode('custom');
      
      console.log('🔍 Découverte personnalisée:', { searchQuery, userId });

      const result = await newsFirebaseService.discoverAndAddNews(
        searchQuery.trim(),
        userId,
        { 
          maxNews: 20,
          language: 'fr',
          country: 'FR'
        }
      );
      
      console.log('📊 Résultat découverte:', result);
      
      if (result.added > 0) {
        setDiscoveryMessage(`${result.added} actualités trouvées`);
        
        // CORRECTION: Recharger ET attendre mise à jour complète
        await loadNewsByTheme();
        
        // Attendre que les nouvelles données soient disponibles
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // CORRECTION: Filtrer avec les nouvelles données
        const updatedAllNews = newsByTheme['all'] || [];
        const customResults = filterNewsBySearch(updatedAllNews, searchQuery);
        
        console.log(`🎯 Post-découverte: ${customResults.length} résultats filtrés`);
        
        setFilteredNews(customResults);
        setCustomSearchResults(customResults);
        
        // CORRECTION: Notification AVEC nouvelles données
        notifyDataUpdate('custom_discovery', { 
          searchQuery: searchQuery,
          discovered: result.discovered || 0,
          added: result.added || 0,
          filtered: customResults.length
        });
        
        setTimeout(() => {
          setDiscoveryMessage('');
          setIsDiscovering(false);
        }, 3000);
        
        return { 
          success: true, 
          dataReady: true,
          discovered: result.discovered || 0,
          added: result.added || 0,
          filtered: customResults.length,
          message: `${result.added} nouvelles actualités trouvées`
        };
        
      } else {
        setDiscoveryMessage('Aucune actualité trouvée');
        
        setTimeout(() => {
          setDiscoveryMessage('');
          setIsDiscovering(false);
        }, 3000);
        
        return { 
          success: false, 
          dataReady: false,
          discovered: result.discovered || 0,
          added: 0,
          message: result.error || 'Aucune actualité trouvée'
        };
      }
      
    } catch (error) {
      console.error('❌ Erreur découverte personnalisée:', error);
      setDiscoveryMessage('Erreur recherche');
      
      setTimeout(() => {
        setDiscoveryMessage('');
        setIsDiscovering(false);
      }, 3000);
      
      return { 
        success: false, 
        dataReady: false, 
        error: error.message,
        message: `Erreur: ${error.message}`
      };
    }
  }, [userId, loadNewsByTheme, filterNewsBySearch, newsByTheme, notifyDataUpdate]);

  // ================================
  // FONCTIONS UTILITAIRES
  // ================================

  const handleReadNews = useCallback(async (newsId) => {
    try {
      await newsFirebaseService.recordNewsView(newsId, userId);
    } catch (error) {
      console.warn('Erreur lecture:', error);
    }
  }, [userId]);

  const handleRefreshObjectiveResults = useCallback(async (objective) => {
    console.log('🔄 Actualisation forcée:', objective.title);
    
    setStoredObjectiveResults(null);
    setHasStoredResults(false);
    
    return await handleDiscoverForObjective(objective, true);
  }, [handleDiscoverForObjective]);

  const getStoredResultsInfo = useCallback(() => {
    if (!storedObjectiveResults) return null;
    
    return {
      createdAt: storedObjectiveResults.createdAt,
      articlesCount: storedObjectiveResults.selectedNewsIds?.length || 0,
      hasSynthesis: !!storedObjectiveResults.synthesis,
      objectiveSnapshot: storedObjectiveResults.objectiveSnapshot,
      analysisMetadata: storedObjectiveResults.analysisMetadata
    };
  }, [storedObjectiveResults]);

  // ================================
  // RETOUR DU HOOK ENRICHI
  // ================================

  return {
    // Données principales
    newsByTheme,
    filteredNews,
    
    // États de chargement
    loading,
    error,
    isDiscovering,
    discoveryMessage,
    
    // Informations d'analyse
    semanticAnalysisInfo,
    analysisProgress,
    
    // Données persistance
    storedObjectiveResults,
    hasStoredResults,
    loadingStoredResults,
    
    // Mode actuel
    currentSearchMode,
    customSearchResults,
    
    // NOUVEAUX: États de notification
    dataUpdated,
    lastUpdateType,
    
    // Fonctions
    loadNews,
    handleDiscoverTheme,
    handleDiscoverForObjective,
    handleCustomDiscovery,
    handleReadNews,
    handleRefreshObjectiveResults,
    getStoredResultsInfo,
    loadStoredObjectiveResults
  };
};